<template>
  <div class="trajectory-config-management">
    <a-card :bordered="false">
      <!-- 查询区域 -->
      <div class="table-page-search-wrapper">
        <a-form layout="inline" @keyup.enter.native="searchQuery">
          <a-row :gutter="24">
            <a-col :md="6" :sm="12">
              <a-form-item label="触发节点">
                <a-select
                    v-model="queryParam.triggerNode"
                    placeholder="请选择触发节点"
                    allow-clear
                    class="wide-select"
                >
                  <a-select-option v-for="item in triggerNodeOptions" :key="item.triggerNodeKey"
                                   :value="item.triggerNodeName">
                    {{ item.triggerNodeName }}
                  </a-select-option>
                </a-select>
              </a-form-item>
            </a-col>

            <template v-if="toggleSearchStatus">
              <a-col :md="6" :sm="8">
                <a-form-item label="订单状态">
                  <a-select
                      v-model="queryParam.changeOrderStatus"
                      placeholder="请选择订单状态"
                      allow-clear
                      class="wide-select"
                  >
                    <a-select-option v-for="item in orderStatusOptions" :key="item.value" :value="item.value">
                      {{ item.label }}
                    </a-select-option>
                  </a-select>
                </a-form-item>
              </a-col>

              <a-col :md="6" :sm="8">
                <a-form-item label="状态">
                  <a-select
                      v-model="queryParam.status"
                      placeholder="请选择状态"
                      allow-clear
                      class="wide-select"
                  >
                    <a-select-option :value="1">启用</a-select-option>
                    <a-select-option :value="0">禁用</a-select-option>
                  </a-select>
                </a-form-item>
              </a-col>
            </template>

            <a-col :md="6" :sm="8">
              <span style="float: left;overflow: hidden;" class="table-page-search-submitButtons">
                <a-button type="primary" @click="searchQuery" icon="search">查询</a-button>
                <a-button @click="searchReset" icon="reload" style="margin-left: 8px">重置</a-button>
                <a @click="handleToggleSearch" style="margin-left: 8px">
                  {{ toggleSearchStatus ? '收起' : '展开' }}
                  <a-icon :type="toggleSearchStatus ? 'up' : 'down'"/>
                </a>
              </span>
            </a-col>
          </a-row>
        </a-form>
      </div>

      <!-- 操作按钮区域 -->
      <div class="table-operator" style="border-top: 5px">
        <a-button @click="showCreateModal" type="primary" icon="plus">新增配置</a-button>
      </div>

      <!-- 表格区域 -->
      <div>
        <a-table
            :columns="columns"
            :dataSource="tableData"
            :pagination="pagination"
            :loading="loading"
            rowKey="id"
            bordered
            @change="handleTableChange"
        >
          <template slot="triggerNode" slot-scope="text">
            {{ getTriggerNodeText(text) }}
          </template>

          <template slot="changeOrderStatus" slot-scope="text">
            {{ text }}
          </template>

          <template slot="customerDisplayed" slot-scope="text">
            <a-tag :color="text == 1 ? 'green' : 'red'">
              {{ text === 1 ? '展示' : '不展示' }}
            </a-tag>
          </template>

          <template slot="status" slot-scope="text, record">
            <a-switch
                :checked="record.status === 1"
                @change="(checked) => handleStatusChange(record, checked)"
                checked-children="启用"
                un-checked-children="禁用"
                size="small"
            />
          </template>

          <template slot="triggerMode" slot-scope="text">
            <a-tag :color="text == 1 ? '#0066FF' : '#CC6600'">
              {{ text == 1 ? '自动触发' : '手动触发' }}
            </a-tag>
          </template>

          <template slot="trajectoryTemplate" slot-scope="text">
            <a-tooltip :title="text" placement="topLeft">
              <div class="template-content">{{ text }}</div>
            </a-tooltip>
          </template>

          <template slot="action" slot-scope="text, record">
            <a-space>
              <a @click="handleEdit(record)">编辑</a>
              <a-divider type="vertical"/>
              <a @click="handleDelete(record.id,record.triggerNode)">删除</a>
            </a-space>
          </template>
        </a-table>
      </div>
    </a-card>

    <!-- 新增/编辑弹窗 -->
    <a-modal
        :visible="modalVisible"
        :title="modalTitle"
        width="800px"
        centered
        :maskClosable="false"
        :confirmLoading="modalLoading"
        @ok="handleSubmit"
        @cancel="handleCancel"
    >
      <a-form
          :form="form"
          :label-col="{ span: 4 }"
          :wrapper-col="{ span: 18 }"
      >
        <a-form-item label="触发节点">
          <a-select
              v-decorator="['triggerNode', { rules: [{ required: true, message: '请选择触发节点' }] }]"
              placeholder="请选择触发节点"
              @change="handleTriggerNodeChange"
              :disabled="!!currentRecord"
          >
            <a-select-option v-for="item in triggerNodeOptions" :key="item.triggerNodeKey"
                             :value="item.triggerNodeName">
              {{ item.triggerNodeName }}
            </a-select-option>
          </a-select>
        </a-form-item>

        <a-form-item label="订单状态">
          <a-select
              v-decorator="['changeOrderStatus', { rules: [{ required: true, message: '请选择订单状态' }] }]"
              placeholder="请选择订单状态"
          >
            <a-select-option v-for="item in orderStatusOptions" :key="item.value" :value="item.value">
              {{ item.label }}
            </a-select-option>
          </a-select>
        </a-form-item>

        <a-form-item label="轨迹模板">
          <MentionInput
              ref="mentionInput"
              :options="options"
              :selectedItem.sync="selectedItem"
              :textareaStr.sync="textareaStr"
              @mention-selected="handleMentionSelected"
              @mention-inputText="handleMentioninputText"
          />
        </a-form-item>

        <a-form-item label="触发方式">
          <a-select
              v-decorator="['triggerMode', { rules: [{ required: true, message: '请选择触发方式' }] }]"
              placeholder="请选择触发方式"
          >
            <a-select-option v-for="item in triggerModeOptions" :key="item.value" :value="item.value">
              {{ item.label }}
            </a-select-option>
          </a-select>
        </a-form-item>

        <a-form-item label="客户展示">
          <a-switch
              v-decorator="['customerDisplayed', { valuePropName: 'checked', initialValue: true }]"
              checked-children="展示"
              un-checked-children="不展示"
          />
        </a-form-item>

        <a-form-item label="状态">
          <a-switch
              v-decorator="['status', { valuePropName: 'checked', initialValue: true }]"
              checked-children="启用"
              un-checked-children="禁用"
          />
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
import {message} from 'ant-design-vue';
import {
  getTrajectoryConfigs,
  createTrajectoryConfig,
  updateTrajectoryConfig,
  deleteTrajectoryConfig,
  updateConfigStatus,
  getTriggerNodes,
  getOrderStatuses,
  getAvailableVariables
} from 'api/trajectory';
import MentionInput from '../../components/MentionInput.vue';

export default {
  name: "trackConfig",
  components: {
    MentionInput
  },
  data() {
    return {
      name: "track",
      searchForm: this.$form.createForm(this),
      queryParam: {},
      toggleSearchStatus: false,
      tableData: [],
      loading: false,
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0,
        showTotal: total => `共 ${total} 条`,
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '50']
      },
      triggerNodeOptions: [],
      availableVariables: [],
      orderStatusOptions: [],
      triggerModeOptions: [
        {value: 1, label: '自动触发'},
        {value: 2, label: '手动触发'}
      ],
      columns: [
        {
          title: '触发节点',
          dataIndex: 'triggerNode',
          key: 'triggerNode',
          width: 180,
          align: "center",
          customRender: (text, row, index) => {
            const obj = {
              children: text,
              attrs: {}
            };

            const rowSpan = this.getRowSpan(this.tableData, 'triggerNode', index);
            obj.attrs.rowSpan = rowSpan > 0 ? rowSpan : 0;
            return obj;
          }
        },
        {
          title: '订单状态',
          dataIndex: 'changeOrderStatus',
          key: 'changeOrderStatus',
          width: 150,
          align: "center",
          scopedSlots: {customRender: 'changeOrderStatus'}
        },
        {
          title: '轨迹模板',
          dataIndex: 'trajectoryTemplate',
          key: 'trajectoryTemplate',
          scopedSlots: {customRender: 'trajectoryTemplate'}
        },
        {
          title: '状态',
          dataIndex: 'status',
          key: 'status',
          width: 100,
          align: "center",
          scopedSlots: {customRender: 'status'}
        },
        {
          title: '客户展示',
          dataIndex: 'customerDisplayed',
          key: 'customerDisplayed',
          width: 100,
          align: "center",
          scopedSlots: {customRender: 'customerDisplayed'}
        },
        {
          title: '触发方式',
          dataIndex: 'triggerMode',
          key: 'triggerMode',
          width: 100,
          align: "center",
          scopedSlots: {customRender: 'triggerMode'}
        },
        {
          title: '操作',
          key: 'action',
          width: 120,
          fixed: 'right',
          align: "center",
          scopedSlots: {customRender: 'action'}
        }
      ],
      modalVisible: false,
      modalLoading: false,
      modalTitle: '新增轨迹配置',
      form: this.$form.createForm(this),
      currentRecord: null,
      selectedItem: '',
      textareaStr: '',
      options: []
    };
  },
  created() {
    this.loadTriggerNodes();
    this.loadData();
    this.loadOrderStatuses();
    this.loadVariables();
  },
  methods: {
    // 新增方法：清空 MentionInput 状态
    clearMentionInput() {
      this.selectedItem = '';
      this.textareaStr = '';
      if (this.$refs.mentionInput && this.$refs.mentionInput.clearContent) {
        this.$refs.mentionInput.clearContent();
      }
    },

    handleMentionSelected(selectedBeans) {
      this.selectedItem = selectedBeans;
      console.log('后端数据:', selectedBeans);
    },

    handleMentioninputText(inputText) {
      this.textareaStr = inputText;
      console.log('前端显示:', inputText);
    },

    async loadTriggerNodes() {
      try {
        const res = await getTriggerNodes();
        this.triggerNodeOptions = res.data || [];
      } catch (error) {
        console.error('加载触发节点失败:', error);
        message.error('加载触发节点失败');
      }
    },

    async loadOrderStatuses() {
      try {
        const res = await getOrderStatuses();
        this.orderStatusOptions = res.data.map(item => ({
          value: item.value,
          label: item.label
        }));
      } catch (error) {
        console.error('加载订单状态失败:', error);
        message.error('加载订单状态失败');
      }
    },

    async loadVariables() {
      try {
        const res = await getAvailableVariables();
        this.options = res.data || [];
      } catch (error) {
        console.error('加载可用变量失败:', error);
        message.error('加载可用变量失败');
      }
    },

    getTriggerNodeText(value) {
      const item = this.triggerNodeOptions.find(opt => opt.value === value);
      return item ? item.label : value;
    },

    getOrderStatusText(code) {
      const item = this.orderStatusOptions.find(opt => opt.value === code);
      return item ? item.label : code;
    },

    getRowSpan(data, key, index) {
      const rowSpanMap = {};
      let count = 1;

      for (let i = 1; i < data.length; i++) {
        if (data[i][key] === data[i - 1][key]) {
          count++;
        } else {
          rowSpanMap[i - count] = count;
          count = 1;
        }
      }

      rowSpanMap[data.length - count] = count;
      return rowSpanMap[index] || 0;
    },

    async loadData() {
      try {
        this.loading = true;
        const params = {
          ...this.queryParam,
          current: this.pagination.current,
          size: this.pagination.pageSize,
          sortField: 'triggerNode',
          sortOrder: 'asc'
        };

        const res = await getTrajectoryConfigs(params);
        this.tableData = res.data.records || [];
        this.pagination.total = res.data.total || 0;
      } catch (error) {
        console.error('加载数据失败:', error);
        message.error('加载数据失败');
      } finally {
        this.loading = false;
      }
    },

    searchQuery() {
      this.pagination.current = 1;
      this.loadData();
    },

    searchReset() {
      this.queryParam = {};
      this.pagination.current = 1;
      this.loadData();
    },

    handleToggleSearch() {
      this.toggleSearchStatus = !this.toggleSearchStatus;
    },

    handleSearch() {
      this.searchQuery();
    },

    handleReset() {
      this.searchReset();
    },

    handleTableChange(pagination) {
      this.pagination.current = pagination.current;
      this.pagination.pageSize = pagination.pageSize;
      this.loadData();
    },

    showCreateModal() {
      this.modalTitle = '新增轨迹配置';
      this.currentRecord = null;
      this.modalVisible = true;
      this.$nextTick(() => {
        this.form.resetFields();
        this.clearMentionInput();
        this.availableVariables = [];
      });
    },

    handleEdit(record) {
      this.modalTitle = '编辑轨迹配置';
      this.currentRecord = record;
      this.modalVisible = true;
      this.$nextTick(async () => {
        this.textareaStr = record.trajectoryTemplate || '';
        this.selectedItem = record.availableVariables || '';
        this.form.setFieldsValue({
          ...record,
          customerDisplayed: record.customerDisplayed === 1,
          status: record.status === 1,
        });
        await this.loadAvailableVariables(record.triggerNode);
      });
    },

    async handleTriggerNodeChange(value) {
      await this.loadAvailableVariables(value);
      const selected = this.triggerNodeOptions.find(item => item.value === value);
      if (selected) {
        this.form.setFieldsValue({
          triggerNodeKey: selected.value.toLowerCase().replace(/_/g, '-')
        });
      }
    },

    async loadAvailableVariables(triggerNode) {
      try {
        const res = await getAvailableVariables({triggerNode});
        this.availableVariables = res.data || [];
      } catch (error) {
        console.error('加载可用变量失败:', error);
        message.error('加载可用变量失败');
      }
    },

    async handleStatusChange(record, checked) {
      const newStatus = checked ? 1 : 0;
      const action = newStatus === 1 ? '启用' : '禁用';

      this.$confirm({
        title: `确认${action}吗？`,
        content: `确定要 ${action} 【${record.triggerNode}】这条配置吗？`,
        okText: '确认',
        cancelText: '取消',
        onOk: async () => {
          try {
            const res = await updateConfigStatus({
              id: record.id,
              status: newStatus
            });
            if (res.code === 200) {
              this.$message.success(`${action}成功`);
              this.loadData(); // 刷新数据
            } else {
              // 返回码非200时显示后端错误信息
              this.$message.error(res.msg || `${action}失败`);
            }
          } catch (error) {
            console.error(`${action}操作异常:`, error);
            // 处理网络错误或系统异常
            this.$message.error(
                error.response?.data?.message ||
                `${action}失败，请稍后重试`
            );
          }
        },
        onCancel: () => {
          this.$message.info(`已取消${action}操作`);
        }
      });
    },

    // 删除配置
    async handleDelete(id,triggerNode) {
      this.$confirm({
        title: '确认删除',
        content: `确定要删除【${triggerNode}】这条轨迹配置吗？删除后数据不可恢复！`,
        okText: '确认',
        cancelText: '取消',
        okButtonProps: {danger: true},  // 设置确认按钮为危险样式
        onOk: async () => {
          try {
            const res = await deleteTrajectoryConfig({id});

            if (res.code === 200) {
              this.$message.success('删除成功');
              this.loadData(); // 刷新数据
            } else {
              // 返回码非200时，显示后端错误信息
              this.$message.error(res.msg || '删除失败');
            }
          } catch (error) {
            console.error('删除失败:', error);
            // 网络错误或系统异常
            this.$message.error(error.message || '删除失败，请稍后重试');
          }
        },
        onCancel: () => {
          this.$message.info('已取消删除操作');
        }
      });
    },

    handleSubmit() {
      this.form.validateFields(async (err, values) => {
        if (err) return;

        this.modalLoading = true;
        try {
          const params = {
            ...values,
            id: this.currentRecord?.id,
            customerDisplayed: values.customerDisplayed ? 1 : 0,
            status: values.status ? 1 : 0,
            trajectoryTemplate: this.textareaStr,
            availableVariables: this.selectedItem
          };

          const response = this.currentRecord
              ? await updateTrajectoryConfig(params)
              : await createTrajectoryConfig(params);

          if (response.code === 200) {
            message.success(this.currentRecord ? '更新成功' : '创建成功');
            this.modalVisible = false;
            this.loadData();
          } else {
            message.error(response.msg || (this.currentRecord ? '更新失败' : '创建失败'));
          }
        } catch (error) {
          console.error('提交失败:', error);
          message.error('提交失败');
        } finally {
          this.modalLoading = false;
        }
      });
    },

    handleCancel() {
      this.form.resetFields();
      this.clearMentionInput();
      this.modalVisible = false;
    }
  }
};
</script>

<style lang="less" scoped>
@import '~@/assets/less/common.less';

.trajectory-config-management {
  .template-content {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.wide-select {
  min-width: 200px !important;
}
</style>
